import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
} from "reactstrap";
import { 
  getReceipts,
  exportCashDepositsDetailsToExcel
} from "../../components/Api/GmsReceipt";
import { ClipLoader } from "react-spinners";
import { Download } from "lucide-react";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI } from "../../helpers/dateHelper";

const emptyFilter = {
  ReceiptId: null,
  BusinessId: null,
  BranchId: null,
  FromDate: null,
  ToDate: null,
};

export default function CashDepositPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(emptyFilter);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsCashDeposit');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddCashDeposit');

  useEffect(() => {
    if (hasPermission) {
      loadReceipts();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const filterData = {
        Type: "cd",
        Page: page,
        PageSize: pageSize,
      };
      
      if (filter.ReceiptId) filterData.ReceiptId = filter.ReceiptId;
      if (filter.BusinessId) filterData.BusinessId = filter.BusinessId;
      if (filter.BranchId) filterData.BranchId = filter.BranchId;
      if (filter.FromDate) filterData.FromDate = formatDateForAPI(filter.FromDate, false);
      if (filter.ToDate) filterData.ToDate = formatDateForAPI(filter.ToDate, false);
      
      const response = await getReceipts(filterData);
      setReceipts(response.receipts || []);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error("Error loading receipts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Export handler
  const handleExport = async () => {
    try {
      setExporting(true);
      // Build filter data for export
      const exportFilter = {
        Type: "cd",
      };
      
      if (filter.ReceiptId) exportFilter.ReceiptId = filter.ReceiptId;
      if (filter.BusinessId) exportFilter.BusinessId = filter.BusinessId;
      if (filter.BranchId) exportFilter.BranchId = filter.BranchId;
      if (filter.FromDate) exportFilter.FromDate = formatDateForAPI(filter.FromDate, false);
      if (filter.ToDate) exportFilter.ToDate = formatDateForAPI(filter.ToDate, false);
      
      await exportCashDepositsDetailsToExcel(exportFilter);
      Swal.fire("Success", "Cash deposits exported successfully", "success");
    } catch (error) {
      console.error("Error exporting cash deposits:", error);
      Swal.fire("Error", "Failed to export cash deposits", "error");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    loadReceipts();
  }, [page, pageSize, filter]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return date;
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const pages = [];
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="d-flex gap-1">
          <Button
            color="primary"
            size="sm"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            Previous
          </Button>
          {pages.map(pageNum => (
            <Button
              key={pageNum}
              color={pageNum === page ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handlePageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
          <Button
            color="primary"
            size="sm"
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Cash Deposits" breadcrumbItem="Cash Deposits" />
          
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Cash Deposits List ({totalCount} records)</h5>
                    <div className="d-flex gap-2">
                      <Button
                        color="success"
                        size="sm"
                        onClick={handleExport}
                        disabled={exporting}
                        className="waves-effect waves-light"
                      >
                        {exporting ? (
                          <>
                            <ClipLoader size={14} color="#fff" className="me-1" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <Download size={14} className="me-1" />
                            Export to Excel
                          </>
                        )}
                      </Button>
                      {hasEditOrHigher && (
                        <Button
                          color="primary"
                          onClick={() => navigate('/GmsAddCashDeposit')}
                        >
                          Add Cash Deposit
                        </Button>
                      )}
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>
                  </div>

                  {/* Receipts List */}
                  {showFilters && (
                        <Row className="mb-3">
                          <Col md={3}>
                            <FormGroup>
                              <Label>From Date</Label>
                              <DatePicker
                                selected={filter.FromDate}
                                onChange={(date) => setFilter(prev => ({ ...prev, FromDate: date }))}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>To Date</Label>
                              <DatePicker
                                selected={filter.ToDate}
                                onChange={(date) => setFilter(prev => ({ ...prev, ToDate: date }))}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>&nbsp;</Label>
                              <div>
                                <Button color="primary" size="sm" onClick={loadReceipts}>Apply</Button>
                                <Button color="secondary" size="sm" className="ms-2" onClick={() => {
                                  setFilter(emptyFilter);
                                  setPage(1);
                                  loadReceipts();
                                }}>Clear</Button>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                      )}

                      {loading ? (
                        <div className="text-center py-4">
                          <ClipLoader color="#556ee6" loading={loading} size={50} />
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <Table bordered>
                              <thead className="table-light">
                                <tr>
                                  <th>Sr.No</th>
                                  <th>Receipt Number</th>
                                  <th>Date</th>
                                  <th>Amount</th>
                                  <th>Bank Account</th>
                                </tr>
                              </thead>
                              <tbody>
                                {receipts.length === 0 ? (
                                  <tr>
                                    <td colSpan="5" className="text-center">No cash deposits found</td>
                                  </tr>
                                ) : (
                                  receipts.map((receipt, index) => {
                                    const receiptId = receipt.receiptId || receipt.ReceiptId;
                                    const receiptNumber = receipt.receiptNumber || receipt.ReceiptNumber || 'N/A';
                                    const receiptDate = receipt.receiptDate || receipt.ReceiptDate;
                                    const amount = receipt.amount || receipt.Amount || 0;
                                    
                                    // Get bank account information
                                    const bankAccountId = receipt.bankAccountId || receipt.BankAccountId;
                                    const bankAccountNumber = receipt.bankAccount_AccountNumber || receipt.BankAccount_AccountNumber || '';
                                    const bankAccountHolderName = receipt.bankAccount_AccountHolderName || receipt.BankAccount_AccountHolderName || '';
                                    
                                    let bankAccountDisplay = 'N/A';
                                    if (bankAccountNumber && bankAccountHolderName) {
                                      bankAccountDisplay = `${bankAccountNumber} - ${bankAccountHolderName}`;
                                    } else if (bankAccountNumber) {
                                      bankAccountDisplay = bankAccountNumber;
                                    } else if (bankAccountHolderName) {
                                      bankAccountDisplay = bankAccountHolderName;
                                    } else if (bankAccountId) {
                                      // If we have bankAccountId but no details, show the ID
                                      bankAccountDisplay = `Bank Account ID: ${bankAccountId}`;
                                    }
                                    
                                    return (
                                      <tr key={receiptId}>
                                        <td>{(page - 1) * pageSize + index + 1}</td>
                                        <td>{receiptNumber}</td>
                                        <td>{formatDate(receiptDate)}</td>
                                        <td>{formatCurrency(amount)}</td>
                                        <td>{bankAccountDisplay}</td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </Table>
                          </div>
                          {totalCount > 0 && <Pagination />}
                        </>
                      )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

